import React, { useRef } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnProps } from 'primereact/column'

import { ProgressSpinner } from 'primereact/progressspinner';
import { getQuestionSetSubmissionHistory, getQuestionSetSubmission } from '../../graphql/questionSets';
import { QuestionSetSubmissionHistory, QuestionSetSubmissionHistoryVariables } from '../../graphql/types/QuestionSetSubmissionHistory';
import {
    QuestionSetSubmission,
    QuestionSetSubmissionVariables,
    QuestionSetSubmission_question_set_submission_question_set_question_pages_question_groups_question_freeforms,
    QuestionSetSubmission_question_set_submission_question_set_question_pages_question_groups_question_multiselects
} from '../../graphql/types/QuestionSetSubmission';
import { value_types_enum } from "./../../types/graphql-global-types";
import { Button } from 'primereact/button';

import jsPDF from "jspdf";

const noWrapStyle = { 'whiteSpace': 'nowrap' };

interface TableRow {
    id: number;
    created_at: any;
    note: string | null;
    email: string;
    isLoading: boolean;
}

export default function QuestionSetsDisplay(props: { match: { params: { id: number } } }) {
    console.log(props.match.params.id);
    const { data, error, loading } = useQuery<QuestionSetSubmissionHistory, QuestionSetSubmissionHistoryVariables>(
        getQuestionSetSubmissionHistory,
        { variables: { questionSetId: props?.match?.params?.id } });

    const setSubmissionsQuery = useQuery<QuestionSetSubmission, QuestionSetSubmissionVariables>(getQuestionSetSubmission);

    const [selectedData, setSelectedData] = React.useState<TableRow[]>(Array<TableRow>())
    const [columns, setColumns] = React.useState<ColumnProps[]>(Array<ColumnProps>());
    const [generetingSubmissionId, setGeneretingSubmissionId] = React.useState<number>(-1)

    let dt = useRef<any>(null);

    React.useEffect(() => {
        if (!data?.question_sets_by_pk) {
            return;
        }

        setColumns([
            { field: 'created_at', header: 'Date', sortable: true },
            { field: 'email', header: 'User', sortable: true },
            { field: 'note', header: 'Submission Note', sortable: true },
            { field: 'actions', header: 'Actions', body: actionsTemplate, style: { textAlign: 'center', width: '10em' } }
        ]);
        const gridData: Array<TableRow> = data?.question_set_submission.map(s => ({
            id: s.id,
            created_at: new Date(s.created_at).toLocaleString(),
            email: s.user.email,
            note: s.note,
            isLoading: s.id == generetingSubmissionId
        }));

        setSelectedData(gridData);
    }, [data, generetingSubmissionId]);

    React.useEffect(() => {
        (async () => {
            if (!setSubmissionsQuery.data) {
                return
            }

            const submissionData = setSubmissionsQuery.data;
            const pdf = await QuestionSubmissionGeneratePdf(submissionData);
            pdf.save("SubmissionSet.pdf");

            setGeneretingSubmissionId(-1);
        })()
    }, [setSubmissionsQuery.data]);

    if (error) {
        console.error(error);
    }

    const onExportCSV = () => {
        dt.current.exportCSV();
    };

    const onPdfExport = async (submissionId: number) => {
        setGeneretingSubmissionId(submissionId);

        setSubmissionsQuery.refetch({
            submissionId: submissionId
        })
    };

    const actionsTemplate = (rowData: TableRow) => (
        <div>
            <Button
                type="button"
                icon={!rowData.isLoading ? "pi pi-download" : "pi pi-spin pi-spinner"}
                className="p-button-primary"
                onClick={(e) => {
                    e.preventDefault();
                    if (generetingSubmissionId == -1) {
                        onPdfExport(rowData.id);
                    }
                }} />
        </div>
    )

    const header = <div style={{ textAlign: 'left' }}><Button type="button" icon="pi pi-external-link" iconPos="left" label="CSV" onClick={onExportCSV}></Button></div>;

    if (loading) {
        return <ProgressSpinner />
    } else {
        return (
            <div className="p-grid">
                <div className="p-col-12">
                    <div className="card card-w-title datatable-demo">
                        <h1>{`Submission Results - ${data?.question_sets_by_pk?.name}`}</h1>
                        <DataTable
                            autoLayout={true}
                            ref={dt}
                            header={header}
                            value={selectedData}
                            selectionMode="single"
                            paginator={true}
                            rows={10}>
                            {columns.map(m => <Column key={m.field} {...m} headerStyle={noWrapStyle} />)}
                        </DataTable>
                    </div>
                </div>
            </div>);
    }
}

const logoSource = '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'

async function QuestionSubmissionGeneratePdf(dataSource: QuestionSetSubmission): Promise<jsPDF> {
    const doc = new jsPDF("p", "mm", "a4");

    const submissionInfo = dataSource.question_set_submission[0];
    const submissionUser = submissionInfo.user;
    const organization = submissionInfo.question_set.organization;
    const setName = submissionInfo.question_set.name
    const submissionPages = submissionInfo.question_set.question_pages;
    const selectedButtonSource = 'iVBORw0KGgoAAAANSUhEUgAAAEgAAABICAYAAABV7bNHAAAABGdBTUEAALGPC/xhBQAAACBjSFJNAAB6JgAAgIQAAPoAAACA6AAAdTAAAOpgAAA6mAAAF3CculE8AAAABmJLR0QA/wD/AP+gvaeTAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAB3RJTUUH5QEZCRkECZBHugAABRtJREFUeNrt21eIXFUcx/HPJEoSFYz1xVhS7L3kIdhSRTaiqBERoojlxUSNGo2gLz5ZIooilkSxkKgpCiKJiYhYoqsiKkjwwYoGNmUtsWRdUsaHMyPXcWbuvTN3ZnYm83vZYTh7/me//M+9v/P/n81NWtWT11VFDWv1Aoa6uoBi1AUUoy6gGHUBxagLKEZdQDHaHQDV5fM6HdBPeAgbap2gkwHtwhO4HVfgfTVkUycDeg9PC1DW4XI8hm1pJulUQL/iPmyB3pmroU/IphvwfdKJOhXQc3iLf+EUfw7m87nnMQtrhG242wH6Ao9iZxFOUb0zV8vl8vAZZuMBDOxOgAawED9UGhCB9jPWYrDahJ0GaCVeLQFRSftiPkZXG9RJgL4TsudvucqDJq3qKX6cjfPiJu0UQDvwCL6E3p7y2ROBcxzmYc+4iTsF0Jt4gURbawRuw4QkE3cCoM2C5/mt2qBI9lwgmMZEandAeTwlOOUk2XMIFmDvpAHaHdDHwnkrXw1OIXuGCS56YpoA7QzoD2Fr9VUbFNlaZ+G6tEHaGdBSrCax51mAg9MGaVdAXwl1nu3VBkWy50oJPE85tSOgQTyIr6mcPRE4xwueZ49agrUjoNexjMSeZz7G1xqs3QBtwP34q9qgSPZciMvqCdhOgHYJFcFPSZQ9Y3CHFJ6nnOoBNCBBwSlDvYNnqA6nxPOcUW/QWgH1YY5CaaEJ+kXwPP3VBkW21tlq8DzlVMuTfSvuFA6Ha5DDpQ0G9CzeJtHWGl1Y30FZBE6bQQO4RzBphEy6Ea80EM7nKpRQo4pkz1WYnlXwNIB24GE8XrLYPswVqnlZa5vw1vqx2qAInBNwsxo9Tz2A8sID8l4MlnQKYKOQSSvU2eot0Qq8VhKrkkYKnmdchvETA1qJu/FnuU5BCaSVGUH6VrGEWkUlnmdWlnCSAlqHW1V5g0QgbZJNJm0XtvP6kvkr6VAZeJ5aAY3APnGDSiDdhOV1QFqLJXFwIp5nDk7PGk5SQBOFHvexkUUlhbSsBkibBM+ztdqgyDrOwbWNgJMUEJyJxTgmBaTNwhslDaQ8nsSHJXNV0n6C5zmw1YDqhfRyQki9BUBJSqgEzzOtUXDSAiKULWuF9FIMpN+FrbWx2gIiMU+UsefJAlAR0iIcnQLSFqFo9WIVSEuEo0sazzO2kXBqBUQ4DC7OENJ64bW+fefwnRXnisS5SAM8T5aAipAW4agUkPoLkJZGIBVLqN/AJ+evjYtb9Dx7DXVAhFfs4qSQhoW7Of24RcHnCEeJFSUg/6eI55mL05oBJwtARUiJMumDnjeKadMv9McXCpeYkpZQz8U1zYKTFaDiwhfhyDhIH/33mXSXUM4YEp6nnHIZ/0Pdu7heTEsmjSKw5wnPquHNBJR10T5xJqWEc5JwdGkqHBrT1ZgsQJpQ8kfWqpHC9d2Ge55yalTbZ7I6IUV+52Jc0nw0QY3si01RfyYdJmRPUzxPOTW6cThFuOA0nuSQCuOGC57n1FbBoTmd1alCJiWC1ErPU07Naj1PlS6T9hc8zwEtI1NQM3vz0wRI4ygPKfLd1QLUlqvZlxcqQop8Plko/Dfd85RTK253TFc5k0YJb60jWg2mqFZdf5kulFbHMnQ8Tzm18n7QDCGTig75cCF7RrUaSlStvkA1Q8ik8UJv65RWAylVQwveCTVDuGc0ptULKaehACgnnNaHpFq9xYa8uoBi1AUUoy6gGHUBxagLKEZdQDH6B2vnbrvET7KUAAAAJXRFWHRkYXRlOmNyZWF0ZQAyMDIxLTAxLTI1VDA5OjI1OjA0LTA1OjAwTiuA6gAAACV0RVh0ZGF0ZTptb2RpZnkAMjAyMS0wMS0yNVQwOToyNTowNC0wNTowMD92OFYAAAAASUVORK5CYII='

    let initialMarginTop = 10

    if (organization.logo_url) {
        initialMarginTop = 40
    }

    let marginTop = initialMarginTop;

    doc.setFontSize(16);
    doc.setFont('', 'bold');

    //////////////
    doc.text(setName, doc.internal.pageSize.width / 2, marginTop, { align: 'center' });
    marginTop += 5;
    //////////////

    //////////////
    doc.rect(20, marginTop, 170, 0.1);
    marginTop += 6;
    //////////////

    //////////////
    doc.setFontSize(10);
    doc.text('Submitted By', 22, marginTop);
    doc.text('Submitted On', 148, marginTop);

    doc.setFont('', '');
    doc.text(submissionUser.email, 50, marginTop);
    doc.text(new Date(submissionInfo.created_at).toLocaleDateString(), 172, marginTop);
    marginTop += 6;
    //////////////

    //////////////
    doc.setFont('', 'bold');
    doc.text('Submission Notes', 22, marginTop);
    doc.setFont('', '');
    addTextToDoc(doc, marginTop, initialMarginTop, 50, submissionInfo.note ? submissionInfo.note : 'N/A', false);
    //////////////

    //////////////
    marginTop += 4;
    doc.rect(20, marginTop, 170, 0.1);
    //////////////

    //////////////
    doc.setFont('', '');
    doc.setFontSize(11);
    let currentMargin = marginTop + 8;
    //////////////

    submissionPages.forEach(page => {
        doc.setFontSize(13);

        //////////////
        doc.setDrawColor(235, 235, 235);
        doc.setFillColor(235, 235, 235);
        doc.rect(20, currentMargin - 5, 170, 7, 'F');
        doc.setDrawColor(0, 0, 0);
        //////////////

        currentMargin = addTextToDoc(doc, currentMargin, initialMarginTop, 21, page.header);

        page.question_groups.forEach(group => {
            //////////////
            doc.setFontSize(13);
            currentMargin = currentMargin + 3
            //////////////

            //////////////
            doc.setDrawColor(218, 218, 218);
            doc.rect(20, currentMargin - 5, 170, 0);
            doc.rect(20, currentMargin + 2, 170, 0);
            doc.setDrawColor(0, 0, 0);
            //////////////

            //////////////
            currentMargin = addTextToDoc(doc, currentMargin, initialMarginTop, 21, group.header) + 3;
            doc.setFontSize(11);
            //////////////

            let questions = (group.question_freeforms as Array<any>)
                .concat(group.question_multiselects as Array<any>);

            questions = questions.sort((a, b) => a.order - b.order);

            questions.forEach(question => {
                if (question.__typename == 'question_freeforms') {
                    let freeFormsQuestion = question as QuestionSetSubmission_question_set_submission_question_set_question_pages_question_groups_question_freeforms;

                    let freeFormAnswer: string = freeFormsQuestion.question_freeform_answers.length > 0 ?
                        freeFormsQuestion.question_freeform_answers[0].value as string
                        : '';

                    //////////////
                    doc.setDrawColor(0, 0, 0);
                    doc.setFillColor(0, 0, 0);
                    doc.rect(20, currentMargin - 2, 1.5, 1.5, 'F');
                    //////////////

                    //////////////
                    doc.setFont('', 'bold');
                    currentMargin = addTextToDoc(doc, currentMargin, initialMarginTop, 23, freeFormsQuestion.text) + 1
                    doc.setFont('', '');
                    //////////////

                    if (freeFormsQuestion.value_type == value_types_enum.Date) {
                        currentMargin = addTextToDoc(doc, currentMargin, initialMarginTop, 23, `${(new Date(freeFormAnswer)).toLocaleDateString()}`);
                    } else if (freeFormsQuestion.value_type == value_types_enum.Number) {
                        currentMargin = addTextToDoc(doc, currentMargin, initialMarginTop, 23, freeFormAnswer);
                    } else if (freeFormsQuestion.value_type == value_types_enum.Text) {
                        currentMargin = addTextToDoc(doc, currentMargin, initialMarginTop, 23, freeFormAnswer);
                    }

                    currentMargin += 1
                } else if (question.__typename == 'question_multiselects') {
                    let multiselectQuestion = question as QuestionSetSubmission_question_set_submission_question_set_question_pages_question_groups_question_multiselects;

                    //////////////
                    doc.setDrawColor(0, 0, 0);
                    doc.setFillColor(0, 0, 0);
                    doc.rect(20, currentMargin - 2, 1.5, 1.5, 'F');
                    //////////////

                    //////////////
                    doc.setFont('', 'bold');
                    currentMargin = addTextToDoc(doc, currentMargin, initialMarginTop, 23, multiselectQuestion.text) + 1
                    doc.setFont('', '');
                    //////////////

                    multiselectQuestion.question_answer_set.question_answer_options.forEach(option => {
                        let isSelected = multiselectQuestion.question_multiselect_answers.some(e => e.value == option.value);

                        if (isSelected) {
                            let optionWidth = doc.getTextWidth(option.header);

                            //////////////
                            doc.setDrawColor(218, 218, 218);
                            doc.rect(22, currentMargin - 4.5, optionWidth + 4 + 12, 7);
                            doc.setDrawColor(0, 0, 0);
                            doc.addImage(selectedButtonSource, 'JPEG', 22 + optionWidth + 10, currentMargin - 3.5, 5, 5);
                            //////////////
                        }

                        currentMargin = addTextToDoc(doc, currentMargin, initialMarginTop, 24, option.header) + 2;
                    })
                }

                currentMargin = currentMargin + 2
            })
        });
    });

    addFooters(doc, organization.display_name, setName, (new Date).toLocaleDateString());
    if (organization.logo_url) {
        await addHeaders(doc, organization.logo_url);
    }

    return doc;
}

const toDataURL = (url: string) => fetch(url)
    .then(response => response.blob())
    .then(blob => new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(blob)
    }))

const addFooters = (doc: any, orgName: string, setName: string, date: string) => {
    const pageCount = doc.internal.getNumberOfPages();
    doc.setFont('helvetica', 'italic')
    doc.setFontSize(8)
    for (var i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        doc.text(setName, 20, 285, { align: 'center' })
        doc.text(date, 195, 285, { align: 'center' })
        doc.text(orgName, doc.internal.pageSize.width / 2, 280, { align: 'center' })
        doc.text('Turn2 Forms - Powered by Mach1 Forms', doc.internal.pageSize.width / 2, 283.5, { align: 'center' })
        doc.text('Page ' + String(i) + ' of ' + String(pageCount), doc.internal.pageSize.width / 2, 287, { align: 'center' })
    }
}

const addHeaders = async (doc: any, orgUrl: string) => {
    const pageCount = doc.internal.getNumberOfPages();
    for (var i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        await addOrgLogo(doc, orgUrl);
    }
}

const addOrgLogo = async (doc: jsPDF, orgUrl: string) => {
    let imgBase64: string = logoSource;// await toDataURL(orgUrl) as string;
    const imgProps = doc.getImageProperties(imgBase64);

    let maxHeight = 20;
    let maxWidth = imgProps.width * maxHeight / imgProps.height

    doc.addImage(imgBase64, 'JPEG', doc.internal.pageSize.width / 2 - maxWidth / 2, 10, maxWidth, maxHeight);
}

const addTextToDoc = (doc: jsPDF, currentMargin: number, defaultMargin: number, leftMargin: number, text: string, isCentre: boolean = false) => {
    var splitText = doc.splitTextToSize(text, 170);

    splitText.forEach((text: string) => {
        doc.text(text, leftMargin, currentMargin, isCentre ? { align: 'center' } : {})
        currentMargin = addPageIfNeeded(doc, currentMargin, defaultMargin);
    });

    return currentMargin;
}

const addPageIfNeeded = (doc: jsPDF, currentMargin: number, defaultMargin: number) => {
    if (currentMargin + 6 >= 275) {
        doc.addPage();
        return defaultMargin;
    }
    return currentMargin + 6;
}